import React, { useState, useEffect } from 'react';
import { X, Package, Hash, Calendar, User, Truck, MapPin, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { POrderDetailsDto, POrderLineDto } from '../../../../types/porders';
import { pOrderLineService } from '../../../../services/pOrderLineService';
import { stcTransService, StcTransDto } from '../../../../services/stcTransService';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface PreviewPOrderModalProps {
  isOpen: boolean;
  orderNo: number | null;
  onClose: () => void;
}

interface LineWithArrived extends POrderLineDto {
  arrivedQuantity: number;
}

export const PreviewPOrderModal: React.FC<PreviewPOrderModalProps> = ({
  isOpen,
  orderNo,
  onClose
}) => {
  const { t } = useTranslation();
  const [orderDetails, setOrderDetails] = useState<POrderDetailsDto | null>(null);
  const [linesWithArrived, setLinesWithArrived] = useState<LineWithArrived[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  useEffect(() => {
    if (isOpen && orderNo) {
      loadOrderDetails();
    }
  }, [isOpen, orderNo]);

  const loadOrderDetails = async () => {
    if (!orderNo) return;

    try {
      setLoading(true);
      setError(null);
      const details = await pOrderLineService.getOrderDetails(orderNo);
      setOrderDetails(details);

      // Hent StcTrans og beregn "Ankommet" for hver linje
      try {
        const batchId = await stcTransService.getOrCreateBatchId(orderNo);
        const allTransactions = await stcTransService.getTransactionsByBatch(batchId);

        const linesWithArrivedData: LineWithArrived[] = details.lines.map(line => {
          const lineTransactions = allTransactions.filter(
            (t: StcTransDto) => t.orderNo === orderNo && t.lineNo === line.lineNo
          );
          const arrivedQty = lineTransactions.reduce(
            (sum: number, t: StcTransDto) => sum + (t.quantity || 0),
            0
          );

          return {
            ...line,
            arrivedQuantity: arrivedQty
          };
        });

        setLinesWithArrived(linesWithArrivedData);
      } catch (err) {
        console.error('Kunne ikke hente StcTrans:', err);
        // Fallback: sett arrivedQuantity til 0
        const linesWithZero: LineWithArrived[] = details.lines.map(line => ({
          ...line,
          arrivedQuantity: 0
        }));
        setLinesWithArrived(linesWithZero);
      }
    } catch (err) {
      console.error('Error loading order details:', err);
      setError(t('warehouse.modules.receipt.errors.loadOrderDetailsFailed', {}, 'Kunne ikke laste ordre-detaljer. Prøv igjen.'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 0: return t('warehouse.modules.receipt.orderStatus.unprocessed', {}, 'Ubehandlet');
      case 10: return t('warehouse.modules.receipt.orderStatus.approved', {}, 'Godkjent');
      case 20: return t('warehouse.modules.receipt.orderStatus.inProgress', {}, 'Under arbeid');
      case 30: return t('warehouse.modules.receipt.orderStatus.processedIncomplete', {}, 'Behandlet, ikke komplett');
      case 40: return t('warehouse.modules.receipt.orderStatus.processedComplete', {}, 'Behandlet, komplett');
      case 45: return t('warehouse.modules.receipt.orderStatus.forPacking', {}, 'Til pakking');
      case 50: return t('warehouse.modules.receipt.orderStatus.readyForExport', {}, 'Klar for eksport');
      case 60: return t('warehouse.modules.receipt.orderStatus.exported', {}, 'Eksportert');
      default: return t('warehouse.modules.receipt.orderStatus.statusCode', { status }, `Status ${status}`);
    }
  };

  const formatDate = (dateString: string | Date | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('no-NO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Få bakgrunnsfarge for rad basert på mottatt status (quantityDelivered)
  const getRowBackgroundColor = (quantityDelivered?: number, totalQuantity?: number) => {
    if (totalQuantity === undefined || totalQuantity === 0) return 'bg-white';

    const delivered = quantityDelivered || 0;

    // Komplett mottatt - Grønn
    if (delivered >= totalQuantity) return 'bg-green-50 hover:bg-green-100';

    // Delvis mottatt - Gul
    if (delivered > 0 && delivered < totalQuantity) return 'bg-yellow-50 hover:bg-yellow-100';

    // Ikke mottatt - Hvit
    return 'bg-white hover:bg-gray-50';
  };

  // Få border-farge for mobile cards
  const getBorderColor = (quantityDelivered?: number, totalQuantity?: number) => {
    if (totalQuantity === undefined || totalQuantity === 0) return 'border-gray-200';

    const delivered = quantityDelivered || 0;

    // Komplett mottatt - Grønn border
    if (delivered >= totalQuantity) return 'border-green-300';

    // Delvis mottatt - Gul border
    if (delivered > 0 && delivered < totalQuantity) return 'border-yellow-300';

    // Ikke mottatt - Grå border
    return 'border-gray-200';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:rounded-lg sm:shadow-xl sm:w-full sm:max-w-7xl sm:h-[90vh] flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Package className="text-blue-600 flex-shrink-0" size={20} />
            <h2 className="text-base sm:text-xl font-semibold text-gray-900 truncate">
              {orderDetails 
                ? t('warehouse.modules.receipt.previewOrder.titleWithNumber', { orderNo: orderDetails.header.orderNoERP }, `Forhåndsvisning - Ordre ${orderDetails.header.orderNoERP}`) 
                : t('warehouse.modules.receipt.previewOrder.title', {}, 'Forhåndsvisning av ordre')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg flex-shrink-0"
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 p-3 sm:p-6 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
              <span className="ml-3 text-gray-600">{t('warehouse.modules.receipt.messages.loadingOrderDetails', {}, 'Laster ordre-detaljer...')}</span>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={loadOrderDetails}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                {t('common.buttons.tryAgain', {}, 'Prøv igjen')}
              </button>
            </div>
          )}

          {orderDetails && (
            <div className="flex-1 flex flex-col space-y-6">
              {/* Collapsible Header Information */}
              <div className="bg-gray-50 rounded-lg overflow-hidden">
                {/* Header Toggle Button */}
                <button
                  onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-100 transition-colors duration-200"
                >
                  <div className="flex items-center gap-2">
                    <Info className="text-blue-600" size={20} />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('warehouse.modules.receipt.sections.headerInformation', {}, 'Hodeinformasjon')}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                      {isHeaderExpanded 
                        ? t('common.buttons.hideDetails', {}, 'Skjul detaljer') 
                        : t('common.buttons.showDetails', {}, 'Vis detaljer')}
                    </span>
                    {isHeaderExpanded ? (
                      <ChevronUp className="text-gray-400" size={20} />
                    ) : (
                      <ChevronDown className="text-gray-400" size={20} />
                    )}
                  </div>
                </button>

                {/* Expandable Content with Scrollbar */}
                <div className={`transition-all duration-300 ease-in-out ${
                  isHeaderExpanded ? 'max-h-96' : 'max-h-0'
                } overflow-hidden`}>
                  <div className="overflow-y-auto max-h-96 px-6 pb-6 border-t border-gray-200">

                    {/* Order Basic Information */}
                    <div className="mt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="flex items-start gap-3">
                          <Hash className="text-gray-400 mt-0.5" size={16} />
                          <div>
                            <span className="text-sm text-gray-500">{t('warehouse.modules.receipt.fields.orderNumberERP', {}, 'Ordrenummer ERP')}:</span>
                            <p className="font-medium">{orderDetails.header.orderNoERP}</p>
                          </div>
                        </div>

                        {orderDetails.header.supplierOrderNo && (
                          <div className="flex items-start gap-3">
                            <Hash className="text-gray-400 mt-0.5" size={16} />
                            <div>
                              <span className="text-sm text-gray-500">{t('warehouse.modules.receipt.fields.supplierOrderNumber', {}, 'Leverandør ordrenr')}:</span>
                              <p className="font-medium">{orderDetails.header.supplierOrderNo}</p>
                            </div>
                          </div>
                        )}

                        <div className="flex items-start gap-3">
                          <Hash className="text-gray-400 mt-0.5" size={16} />
                          <div>
                            <span className="text-sm text-gray-500">{t('common.fields.status', {}, 'Status')}:</span>
                            <p className="font-medium">{getStatusText(orderDetails.header.orderStatus)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Supplier Information */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <Truck size={16} className="text-blue-600" />
                        {t('warehouse.modules.receipt.supplier', {}, 'Leverandør')}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {orderDetails.header.supplierName && (
                          <div className="flex items-start gap-3">
                            <User className="text-gray-400 mt-0.5" size={16} />
                            <div>
                              <span className="text-sm text-gray-500">{t('common.fields.name', {}, 'Navn')}:</span>
                              <p className="font-medium">{orderDetails.header.supplierName}</p>
                            </div>
                          </div>
                        )}

                        {orderDetails.header.supplierNo && (
                          <div className="flex items-start gap-3">
                            <Hash className="text-gray-400 mt-0.5" size={16} />
                            <div>
                              <span className="text-sm text-gray-500">{t('warehouse.modules.receipt.fields.supplierNumber', {}, 'Leverandørnummer')}:</span>
                              <p className="font-medium">{orderDetails.header.supplierNo}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Dates and Warehouse */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="flex items-start gap-3">
                          <Calendar className="text-gray-400 mt-0.5" size={16} />
                          <div>
                            <span className="text-sm text-gray-500">{t('warehouse.modules.receipt.fields.orderDate', {}, 'Ordredato')}:</span>
                            <p className="font-medium">{formatDate(orderDetails.header.orderDate)}</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <Calendar className="text-gray-400 mt-0.5" size={16} />
                          <div>
                            <span className="text-sm text-gray-500">{t('warehouse.modules.receipt.fields.deliveryDate', {}, 'Leveringsdato')}:</span>
                            <p className="font-medium">{formatDate(orderDetails.header.deliveryDate)}</p>
                          </div>
                        </div>

                        {orderDetails.header.warehouse && (
                          <div className="flex items-start gap-3">
                            <MapPin className="text-gray-400 mt-0.5" size={16} />
                            <div>
                              <span className="text-sm text-gray-500">{t('warehouse.general.warehouse', {}, 'Lager')}:</span>
                              <p className="font-medium">{orderDetails.header.warehouse}</p>
                            </div>
                          </div>
                        )}

                        {orderDetails.header.projectNo && (
                          <div className="flex items-start gap-3">
                            <Hash className="text-gray-400 mt-0.5" size={16} />
                            <div>
                              <span className="text-sm text-gray-500">{t('warehouse.modules.receipt.fields.projectNo', {}, 'Prosjektnr')}:</span>
                              <p className="font-medium">{orderDetails.header.projectNo}</p>
                            </div>
                          </div>
                        )}

                        {orderDetails.header.depNo && (
                          <div className="flex items-start gap-3">
                            <Hash className="text-gray-400 mt-0.5" size={16} />
                            <div>
                              <span className="text-sm text-gray-500">{t('warehouse.modules.receipt.fields.depNo', {}, 'Avdeling')}:</span>
                              <p className="font-medium">{orderDetails.header.depNo}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Free Fields */}
                    {(orderDetails.header.orderFreeField1 || orderDetails.header.orderFreeField2 || orderDetails.header.orderFreeField3) && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('warehouse.modules.receipt.sections.additionalInformation', {}, 'Tilleggsinformasjon')}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {orderDetails.header.orderFreeField1 && (
                            <div>
                              <span className="text-sm text-gray-500">{t('warehouse.general.freeField1', {}, 'Frifelt 1')}:</span>
                              <p className="font-medium">{orderDetails.header.orderFreeField1}</p>
                            </div>
                          )}
                          {orderDetails.header.orderFreeField2 && (
                            <div>
                              <span className="text-sm text-gray-500">{t('warehouse.general.freeField2', {}, 'Frifelt 2')}:</span>
                              <p className="font-medium">{orderDetails.header.orderFreeField2}</p>
                            </div>
                          )}
                          {orderDetails.header.orderFreeField3 && (
                            <div>
                              <span className="text-sm text-gray-500">{t('warehouse.general.freeField3', {}, 'Frifelt 3')}:</span>
                              <p className="font-medium">{orderDetails.header.orderFreeField3}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Delivery Information */}
                    {(orderDetails.header.delCustomerName || orderDetails.header.delAddress1 || orderDetails.header.delCity) && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <MapPin size={16} className="text-blue-600" />
                          {t('warehouse.modules.receipt.sections.deliveryInformation', {}, 'Leveringsinformasjon')}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {orderDetails.header.delCustomerName && (
                            <div>
                              <span className="text-sm text-gray-500">{t('warehouse.general.customerName', {}, 'Kundenavn')}:</span>
                              <p className="font-medium">{orderDetails.header.delCustomerName}</p>
                            </div>
                          )}
                          {(orderDetails.header.delAddress1 || orderDetails.header.delAddress2 || orderDetails.header.delZipCode || orderDetails.header.delCity) && (
                            <div>
                              <span className="text-sm text-gray-500">{t('common.fields.address', {}, 'Adresse')}:</span>
                              <div className="font-medium">
                                {orderDetails.header.delAddress1 && <p>{orderDetails.header.delAddress1}</p>}
                                {orderDetails.header.delAddress2 && <p>{orderDetails.header.delAddress2}</p>}
                                {(orderDetails.header.delZipCode || orderDetails.header.delCity) && (
                                  <p>{orderDetails.header.delZipCode} {orderDetails.header.delCity}</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Statistics Boxes */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="bg-green-50 rounded-lg p-2 sm:p-4 border-2 border-green-200">
                  <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-0">
                    <Package className="h-6 w-6 sm:h-8 sm:w-8 text-green-500" />
                    <div className="sm:ml-3 text-center sm:text-left">
                      <p className="text-xs sm:text-sm font-medium text-green-600">{t('warehouse.modules.receipt.statistics.linesReceived', {}, 'Antall linjer mottatt')}</p>
                      <p className="text-xl sm:text-2xl font-bold text-green-900">
                        {linesWithArrived.filter(line =>
                          (line.quantityDelivered || 0) >= (line.quantity || 0) && (line.quantity || 0) > 0
                        ).length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-2 sm:p-4 border-2 border-yellow-200">
                  <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-0">
                    <Package className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-500" />
                    <div className="sm:ml-3 text-center sm:text-left">
                      <p className="text-xs sm:text-sm font-medium text-yellow-600">{t('warehouse.modules.receipt.statistics.partiallyReceived', {}, 'Delvis mottatt')}</p>
                      <p className="text-xl sm:text-2xl font-bold text-yellow-900">
                        {linesWithArrived.filter(line => {
                          const delivered = line.quantityDelivered || 0;
                          const ordered = line.quantity || 0;
                          return delivered > 0 && delivered < ordered;
                        }).length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 sm:p-4 border-2 border-gray-300">
                  <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-0">
                    <Package className="h-6 w-6 sm:h-8 sm:w-8 text-gray-500" />
                    <div className="sm:ml-3 text-center sm:text-left">
                      <p className="text-xs sm:text-sm font-medium text-gray-600">{t('warehouse.modules.receipt.statistics.notReceived', {}, 'Ikke mottatt')}</p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900">
                        {linesWithArrived.filter(line =>
                          (line.quantityDelivered || 0) === 0
                        ).length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lines Information - Desktop Table */}
              <div className="flex-1 flex flex-col">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mb-4">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Package size={18} className="sm:hidden" />
                    <Package size={20} className="hidden sm:block" />
                    {t('warehouse.modules.receipt.orderLines', {}, 'Ordrelinjer')} ({orderDetails.lines.length})
                  </h3>

                  {/* Fargekode-forklaring */}
                  <div className="flex items-center gap-2 sm:gap-3 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 bg-green-50 border border-green-300 rounded"></div>
                      <span className="text-gray-600">{t('warehouse.modules.receipt.legend.complete', {}, 'Komplett')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 bg-yellow-50 border border-yellow-300 rounded"></div>
                      <span className="text-gray-600">{t('warehouse.modules.receipt.legend.partial', {}, 'Delvis')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 bg-white border border-gray-200 rounded"></div>
                      <span className="text-gray-600">{t('warehouse.modules.receipt.legend.notReceived', {}, 'Ikke mottatt')}</span>
                    </div>
                  </div>
                </div>

                {/* Desktop Table View */}
                <div className="hidden lg:block bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('warehouse.general.itemNumber', {}, 'Varenummer')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('common.fields.description', {}, 'Beskrivelse')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('warehouse.modules.receipt.fields.received', {}, 'Mottatt')}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('warehouse.modules.receipt.fields.arrived', {}, 'Ankommet')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {linesWithArrived.map((line) => (
                          <tr key={line.lineNo} className={`transition-colors ${getRowBackgroundColor(line.quantityDelivered, line.quantity)}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {line.itemNo}
                              </div>
                              <div className="text-xs text-gray-500">
                                {t('warehouse.modules.receipt.lineNumber', { lineNo: line.lineNo }, `Linje ${line.lineNo}`)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {line.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {line.quantity || 0} {line.itemUnitTxt}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {line.quantityDelivered || 0} {line.itemUnitTxt}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {line.arrivedQuantity} {line.itemUnitTxt}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile/Tablet Card View */}
                <div className="lg:hidden space-y-2">
                  {linesWithArrived.map((line) => {
                    const bestilt = line.quantity || 0;
                    const mottatt = line.quantityDelivered || 0;
                    const ankommet = line.arrivedQuantity || 0;

                    // Bestem bakgrunnsfarge basert på mottatt status
                    let bgColor = 'bg-white';
                    if (mottatt >= bestilt && bestilt > 0) {
                      bgColor = 'bg-green-50';
                    } else if (mottatt > 0 && mottatt < bestilt) {
                      bgColor = 'bg-yellow-50';
                    }

                    return (
                      <div key={line.lineNo} className={`${bgColor} border border-gray-200 rounded-lg shadow-sm p-3`}>
                        {/* Linje 1: Varenummer, Beskrivelse */}
                        <div className="mb-2">
                          <div className="flex items-baseline space-x-2">
                            <span className="font-semibold text-gray-900 text-sm">
                              {line.itemNo}
                            </span>
                            <span className="text-xs text-gray-500">
                              - {line.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}
                            </span>
                          </div>
                        </div>

                        {/* Linje 2: Bestilt, Mottatt, Ankommet */}
                        <div className="flex items-center space-x-4 text-xs text-gray-700">
                          <span>
                            <span className="text-gray-500">{t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}:</span> <span className="font-medium">{bestilt}</span>
                          </span>
                          <span>
                            <span className="text-gray-500">{t('warehouse.modules.receipt.fields.received', {}, 'Mottatt')}:</span> <span className="font-medium">{mottatt}</span>
                          </span>
                          <span>
                            <span className="text-gray-500">{t('warehouse.modules.receipt.fields.arrived', {}, 'Ankommet')}:</span> <span className="font-medium">{ankommet}</span>
                          </span>
                          <span className="text-gray-500">{line.itemUnitTxt}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
